import MarkdownIt from "markdown-it";
import markdownItAttrs from "markdown-it-attrs";
import markdownItMark from "markdown-it-mark";
import { toggleMark } from "prosemirror-commands";
import { Schema } from "prosemirror-model";
import { EditorState } from "prosemirror-state";

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type { MenuFunction } from "@/wc/text-editor/lib/core/menu";
import type {
    MarkdownSerializerConfig,
    ParseSpecMap,
} from "@/wc/text-editor/lib/core/types";

// `null` is the default highlight (no colour class).
const COLORS: Array<{ color: string | null; label: string }> = [
    { color: null, label: "Yellow" },
    { color: "purple", label: "Purple" },
    { color: "green", label: "Green" },
    { color: "pink", label: "Pink" },
];

const colorFromClass = (cls: string | null): string | null => {
    const match = (cls || "").match(/hl-(\w+)/);
    return match ? match[1] : null;
};

// Whether the selection carries a highlight of exactly `color`.
const highlightColorActive = (state: EditorState, color: string | null) => {
    const type = state.schema.marks.highlight;
    const { empty, $from, from, to } = state.selection;
    const matches = (
        marks: readonly { type: unknown; attrs: { color?: string | null } }[]
    ) =>
        marks.some((m) => m.type === type && (m.attrs.color ?? null) === color);
    if (empty) {
        return matches(state.storedMarks || $from.marks());
    }
    let active = false;
    state.doc.nodesBetween(from, to, (node) => {
        if (matches(node.marks)) active = true;
    });
    return active;
};

export class HighlightMarkExtension extends BaseExtension {
    // Enable ==mark== and {.class} attribute lists in MarkdownIt.
    modifyMarkdownIt = (base: MarkdownIt): MarkdownIt => {
        return base
            .use(markdownItMark)
            .use(markdownItAttrs, { allowedAttributes: ["class"] });
    };

    // Highlight mark carries a colour, rendered as <mark class="hl-<color>">.
    modifySchema = (currentSchema: Schema) => {
        return new Schema({
            nodes: currentSchema.spec.nodes,
            marks: currentSchema.spec.marks.addToEnd("highlight", {
                attrs: { color: { default: null } },
                parseDOM: [
                    {
                        tag: "mark",
                        getAttrs: (dom) => ({
                            color: colorFromClass(
                                (dom as HTMLElement).getAttribute("class")
                            ),
                        }),
                    },
                ],
                toDOM(mark) {
                    return mark.attrs.color
                        ? ["mark", { class: `hl-${mark.attrs.color}` }]
                        : ["mark"];
                },
                inclusive: false,
            }),
        });
    };

    modifyParser = (currentParser: ParseSpecMap): ParseSpecMap => {
        return {
            ...currentParser,
            mark: {
                mark: "highlight",
                getAttrs: (token) => ({
                    color: colorFromClass(token.attrGet("class")),
                }),
            },
        };
    };

    modifySerializer = (currentConfig: MarkdownSerializerConfig) => {
        return {
            ...currentConfig,
            marks: {
                ...currentConfig.marks,
                highlight: {
                    open: "==",
                    close: (
                        _state: unknown,
                        mark: { attrs: { color?: string | null } }
                    ) =>
                        mark.attrs.color ? `=={.hl-${mark.attrs.color}}` : "==",
                    mixable: true,
                    expelEnclosingWhitespace: true,
                },
            },
        };
    };

    menu?: (currentMenu: MenuFunction) => MenuFunction = (
        currentMenuFunction
    ) => {
        return (view) => {
            const type = view.state.schema.marks.highlight;
            const items = COLORS.map(({ color, label }) => ({
                type: "toggle" as const,
                run: (state, dispatch, view) => {
                    const { empty, from, to } = state.selection;
                    // toggleMark keys off the mark TYPE only, so switching colour
                    // would clear the highlight; recolour by replacing the mark.
                    if (highlightColorActive(state, color)) {
                        toggleMark(type, { color })(state, dispatch);
                    } else if (empty) {
                        dispatch(
                            state.tr
                                .removeStoredMark(type)
                                .addStoredMark(type.create({ color }))
                        );
                    } else {
                        dispatch(
                            state.tr
                                .removeMark(from, to, type)
                                .addMark(from, to, type.create({ color }))
                                .scrollIntoView()
                        );
                    }
                    view.focus();
                },
                label,
                name: `highlight-${color ?? "yellow"}`,
                active: (state) => highlightColorActive(state, color),
            }));
            return [
                ...currentMenuFunction(view),
                {
                    type: "dropdown",
                    name: "highlight",
                    icon: { name: "highlighter" },
                    label: "Highlight",
                    caret: true,
                    order: 27,
                    group: "base",
                    items,
                    active: (state) =>
                        COLORS.some((c) =>
                            highlightColorActive(state, c.color)
                        ),
                },
            ];
        };
    };
}
